/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import { Hidden, Typography2 } from '@coursera/cds-core';

import useIsOnLocaleSubfolderRoute from 'bundles/internationalization-lib/hooks/useIsOnLocaleSubfolderRoute';
import SearchResultsHeader from 'bundles/search-common/components/SearchResultsHeader';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import InlineSearchFilters from 'bundles/search-common/components/filters/InlineSearchFilters';
import SearchFilters from 'bundles/search-common/components/filters/SearchFilters';
import useFilterListOrder from 'bundles/search-common/components/filters/useFilterListOrder';
import { SEARCH_FILTERS_INDEX_ID } from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData } from 'bundles/search-common/types';
import { createFilterKeyToOptionsMap, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';
import { styles as SearchPageBodyStyles } from 'bundles/search-v2/components/SearchPageBody';

import _t from 'i18n!nls/search';

type Props = {
  isMobile: boolean;
  isMobileOrTabletView: boolean;
  optimisticActiveFilterCount: number;
  isSEOEntityPage: boolean;
  showNoResultsRecommendations: boolean;
  eventingData: SearchPageEventingData;
  headerIsScrolledUp: boolean;
  paginationType: 'infinite' | 'control';
};

const styles = {
  stickyContainer: css`
    height: 100%;
    width: 100%;
    min-height: 1000px;
    min-width: 50px;
    align-self: flex-start;
    padding-right: 16px; /* give space for auto-hide scrollbar */
  `,
  stickyFilterWrapper: css`
    position: sticky;
    top: 120px;
    padding-bottom: 32px;
  `,
  /* because the header changes height when scrolling up */
  stickyFilterWrapperWhenPageIsScrolled: css`
    top: 80px;
  `,
  scrollableFilters: css`
    height: calc(100vh - 180px); /* viewport height minus full heading height plus padding */
    overflow-y: auto;
    visibility: hidden; /* auto-hide scrollbar for filters */
    padding-right: 8px;
    padding-left: 3px;
    transition: visibility 0.7s; /* scrollbar hide delay */

    /* the magic that shows the scrollbar on hover */
    &:hover,
    &:focus,
    .filterContent {
      visibility: visible;
    }
  `,
  scrollableFiltersWhenPageIsScrolled: css`
    height: calc(100vh - 145px); /* viewport height minus shrunk heading height plus padding */
  `,
};

function scrollUpOnFilterChange(disableScrollAnimation: boolean) {
  scrollUpIfNecessary({
    disableScrollAnimation,
    mustBeScrolledPastThisElementId: 'search-results-header-wrapper',
  });
}

const SearchPageFilters = ({
  isMobile,
  isMobileOrTabletView,
  optimisticActiveFilterCount,
  isSEOEntityPage,
  showNoResultsRecommendations,
  eventingData,
  headerIsScrolledUp,
  paginationType,
}: Props) => {
  const { query: queryParams } = useLocation();
  const { query: searchQueryFromParams } = queryParams;
  const isOnLocaleSubfolderRoute = useIsOnLocaleSubfolderRoute();
  const showSortByFilter = !isSEOEntityPage;
  const {
    getSearchResults,
    addFilters,
    removeFilters,
    clearFilters,
    setFiltersByFacet,
    setSortBy,
    isLoading,
    activeIndex,
    sortBy,
  } = useSearch();
  const searchData = getSearchResults(activeIndex);
  const filterData = getSearchResults(SEARCH_FILTERS_INDEX_ID);
  const { facets, pagination, facetFilters, hitsDisplayedCount = 0 } = searchData || {};
  const allFilters = filterData?.facets;
  const totalHits = pagination?.totalElements || undefined;
  const activeFilters = useMemo(() => [...new Set(facetFilters?.flat())], [facetFilters]);
  const filterListOrder = useFilterListOrder(DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE);
  const filterKeyToOptionsMap = useMemo(
    () => createFilterKeyToOptionsMap(facets, facetFilters, filterListOrder, allFilters),
    [allFilters, facetFilters, facets, filterListOrder]
  );

  const shouldShowInfiniteStylePagination = paginationType === 'infinite';
  const shouldShowStickySidebarFilters =
    !showNoResultsRecommendations && shouldShowInfiniteStylePagination && !isMobileOrTabletView;

  const isOnEQPSubfolderRoute = isOnLocaleSubfolderRoute && isSEOEntityPage;

  return (
    <>
      <SearchResultsHeader
        searchQuery={simplifyQueryValue(searchQueryFromParams)}
        numberOfHits={totalHits || undefined}
        shouldHide={!isMobile}
        isLoading={isLoading && hitsDisplayedCount === 0}
        activeFilterCount={optimisticActiveFilterCount}
        lessenHeaderFocus={true}
        hideHits={true}
      />
      <div
        css={[
          SearchPageBodyStyles.header,
          isMobile && SearchPageBodyStyles.headerMobile,
          SearchPageBodyStyles.fullHeight,
        ]}
      >
        <div css={[SearchPageBodyStyles.mobileSortByLayoutShim, SearchPageBodyStyles.fullHeight]}>
          {/* where the mobile problem starts */}
          <Hidden smDown css={[shouldShowStickySidebarFilters && styles.stickyContainer]}>
            <div
              css={[
                shouldShowStickySidebarFilters && styles.stickyFilterWrapper,
                shouldShowStickySidebarFilters && headerIsScrolledUp && styles.stickyFilterWrapperWhenPageIsScrolled,
              ]}
            >
              {shouldShowStickySidebarFilters && (
                <Typography2 variant="subtitleLarge" component="h3" css={{ marginTop: '8px', marginBottom: '32px' }}>
                  {_t('Filter by')}
                </Typography2>
              )}
              <div
                css={[
                  shouldShowStickySidebarFilters && styles.scrollableFilters,
                  shouldShowStickySidebarFilters && headerIsScrolledUp && styles.scrollableFiltersWhenPageIsScrolled,
                ]}
              >
                <div className="filterContent">
                  {!showNoResultsRecommendations && !isMobile && shouldShowInfiniteStylePagination && (
                    <InlineSearchFilters
                      eventingData={eventingData}
                      shouldShowSectionTitle={!shouldShowInfiniteStylePagination}
                      onFilterValueChange={() => scrollUpOnFilterChange(true)}
                    />
                  )}
                </div>
              </div>
            </div>
          </Hidden>
          {(!shouldShowInfiniteStylePagination || isMobile) && (
            <SearchFilters
              filters={filterKeyToOptionsMap}
              activeFilters={activeFilters}
              eventingData={eventingData}
              isMobile={isMobile}
              showingNoResults={!!showNoResultsRecommendations}
              addFilters={(filter: string) => addFilters(activeIndex, [filter])}
              setFiltersByFacet={(facetName: string, newFilters: string[]) => {
                scrollUpOnFilterChange(false);
                setFiltersByFacet(activeIndex, facetName, newFilters);
              }}
              removeFilters={(filter: string) => removeFilters(activeIndex, [filter])}
              clearFilters={() => clearFilters(activeIndex)}
              filtersListOrder={filterListOrder}
              shouldHideSkillsFilter={isOnEQPSubfolderRoute}
            />
          )}
        </div>
        {/* mobile sortby */}
        {isMobile && showSortByFilter && !showNoResultsRecommendations && (
          <div>
            <SortByIndex changeIndex={setSortBy} sortByValue={sortBy} />
          </div>
        )}
      </div>
    </>
  );
};

export default SearchPageFilters;
